import React, { useState, useEffect, ReactNode, useRef } from "react";
import styles from "./Slider.module.scss";

interface SliderProps {
    children: ReactNode[];   // array of components: HotelCard, RoomCard, etc.
    slidesToShow?: number;   // optional default number of visible slides
    /** Native scroll + mouse/touch drag instead of the click-paged
     * transform track — for rows that can grow past a fixed slide count
     * (e.g. fare tiers), where snapping 3-at-a-time doesn't fit any total. */
    draggable?: boolean;
    /** Hide the prev/next arrow buttons. Only meaningful with draggable —
     * a drag-only row has no other affordance implying it scrolls, so pair
     * this with a peek at the next card's edge in the surrounding layout. */
    showArrows?: boolean;
}

export const Slider: React.FC<SliderProps> = ({ children, draggable = false, showArrows = true }) => {
    if (draggable) {
        return <DragSlider showArrows={showArrows}>{children}</DragSlider>;
    }
    return <PagedSlider>{children}</PagedSlider>;
};

/** Original behaviour: transform-paged, exactly 3 slides per page. */
const PagedSlider: React.FC<{ children: ReactNode[] }> = ({ children }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [stepPx, setStepPx] = useState(0);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const visibleSlides = 3;
    const maxIndex = Math.max(0, children.length - visibleSlides);
    const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
    const goNext = () => setCurrentIndex((i) => Math.min(maxIndex, i + 1));

    useEffect(() => {
        const recalcStep = () => {
            const track = trackRef.current;
            if (!track) return;
            const firstSlide = track.querySelector<HTMLElement>(`.${styles.slide}`);
            if (!firstSlide) return;
            const slideWidth = firstSlide.getBoundingClientRect().width;
            const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0') || 0;
            setStepPx(slideWidth + gap);
        };

        recalcStep();
        window.addEventListener('resize', recalcStep);
        return () => window.removeEventListener('resize', recalcStep);
    }, [children.length]);

    return (
        <div className={styles.sliderContainer}>
            <button onClick={goPrev} disabled={currentIndex === 0} className={styles.arrow}>
                ◀
            </button>

            <div className={styles.trackWrapper}>
                <div
                    ref={trackRef}
                    className={styles.track}
                    style={{
                        transform: `translateX(-${currentIndex * stepPx}px)`,
                    }}
                >
                    {children.map((child, i) => (
                        <div key={i} className={styles.slide}>
                            {child}
                        </div>
                    ))}
                </div>
            </div>

            <button onClick={goNext} disabled={currentIndex >= maxIndex} className={styles.arrow}>
                ▶
            </button>
        </div>
    );
};

/** Native-scroll track the user drags with the mouse (or a finger) instead
 * of paging through fixed 3-slide steps — the row scrolls exactly as far
 * as the pointer moves, so it works the same whether there are 3 fare
 * tiers or 8. Arrow buttons, if shown, just scroll by one slide's width. */
const DragSlider: React.FC<{ children: ReactNode[]; showArrows: boolean }> = ({ children, showArrows }) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [atStart, setAtStart] = useState(true);
    const [atEnd, setAtEnd] = useState(false);

    // Drag bookkeeping lives in refs, not state — every value here changes
    // mid-gesture, and re-rendering on each pointer-move would fight the
    // browser's own scroll repaint.
    const dragging = useRef(false);
    const startX = useRef(0);
    const startScrollLeft = useRef(0);
    const dragDistance = useRef(0);

    const updateEdges = () => {
        const el = wrapperRef.current;
        if (!el) return;
        setAtStart(el.scrollLeft <= 1);
        setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 1);
    };

    useEffect(() => {
        updateEdges();
        const el = wrapperRef.current;
        if (!el) return undefined;
        const onScroll = () => updateEdges();
        el.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [children.length]);

    const beginDrag = (clientX: number) => {
        const el = wrapperRef.current;
        if (!el) return;
        dragging.current = true;
        dragDistance.current = 0;
        startX.current = clientX;
        startScrollLeft.current = el.scrollLeft;
    };

    const moveDrag = (clientX: number) => {
        const el = wrapperRef.current;
        if (!el || !dragging.current) return;
        const delta = clientX - startX.current;
        dragDistance.current = Math.max(dragDistance.current, Math.abs(delta));
        el.scrollLeft = startScrollLeft.current - delta;
    };

    const endDrag = () => {
        dragging.current = false;
    };

    // A drag that ends over a card would otherwise also fire that card's
    // onClick (e.g. selecting a fare tier) — swallow the click only when
    // the gesture actually moved, so a plain tap still selects normally.
    const onClickCapture = (e: React.MouseEvent) => {
        if (dragDistance.current > 5) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const stepBy = (direction: 1 | -1) => {
        const el = wrapperRef.current;
        if (!el) return;
        const firstSlide = el.querySelector<HTMLElement>(`.${styles.slide}`);
        const slideWidth = firstSlide?.getBoundingClientRect().width || el.clientWidth / 3;
        const gap = Number.parseFloat(window.getComputedStyle(el).columnGap || '0') || 0;
        el.scrollBy({ left: direction * (slideWidth + gap), behavior: 'smooth' });
    };

    return (
        <div className={styles.sliderContainer}>
            {showArrows && (
                <button onClick={() => stepBy(-1)} disabled={atStart} className={styles.arrow}>
                    ◀
                </button>
            )}

            <div
                ref={wrapperRef}
                className={`${styles.trackWrapper} ${styles.trackWrapperDrag}`}
                onMouseDown={(e) => { e.preventDefault(); beginDrag(e.clientX); }}
                onMouseMove={(e) => moveDrag(e.clientX)}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                onTouchStart={(e) => beginDrag(e.touches[0].clientX)}
                onTouchMove={(e) => moveDrag(e.touches[0].clientX)}
                onTouchEnd={endDrag}
                onClickCapture={onClickCapture}
            >
                <div className={styles.track}>
                    {children.map((child, i) => (
                        <div key={i} className={styles.slide}>
                            {child}
                        </div>
                    ))}
                </div>
            </div>

            {showArrows && (
                <button onClick={() => stepBy(1)} disabled={atEnd} className={styles.arrow}>
                    ▶
                </button>
            )}
        </div>
    );
};
